import { environment } from '@environments/environment';

/**
 * Which of the two client surfaces this browser is on.
 *
 * The estimator and the briefings are one Angular build served on two hosts
 * (see docs/superpowers/specs/2026-09-08-briefings-domain-split-design.md).
 * Everything that differs between them -- which routes match, where a link
 * points, which Plausible domain is reported -- is derived from this.
 *
 * These are plain functions rather than an injectable because `main.ts` needs
 * the answer before `bootstrapApplication` runs, where there is no injector.
 * `AppSurfaceService` wraps them for everything inside DI.
 */
export type AppSurface = 'estimator' | 'briefings';

export interface AppOrigins {
  readonly estimator: string;
  readonly briefings: string;
}

export const APP_ORIGINS: AppOrigins = {
  estimator: environment.estimatorOrigin,
  briefings: environment.briefingsOrigin,
};

/**
 * The host of an origin string, or `''` if it does not parse.
 *
 * Exported because `main.ts` runs before `bootstrapApplication` and reads these
 * same hand-maintained environment values: an empty or malformed origin there
 * would throw at module scope and leave the app server-rendered but never
 * hydrated, on both hosts.
 */
export function hostOf(origin: string): string {
  try {
    return new URL(origin).host.toLowerCase();
  } catch {
    return '';
  }
}

/**
 * The estimator is the default for anything unrecognised -- a preview host, an
 * IP, `localhost:4200`. It is the surface that serves every screen, so guessing
 * it wrong costs a redirect; guessing "briefings" wrong would hide the app.
 */
export function surfaceForHost(host: string, origins: AppOrigins): AppSurface {
  return host.toLowerCase() === hostOf(origins.briefings) ? 'briefings' : 'estimator';
}

export function originFor(surface: AppSurface, origins: AppOrigins): string {
  return surface === 'briefings' ? origins.briefings : origins.estimator;
}

export function absoluteUrl(surface: AppSurface, path: string, origins: AppOrigins): string {
  const origin = originFor(surface, origins).replace(/\/$/, '');
  const rooted = path.startsWith('/') ? path : `/${path}`;
  return `${origin}${rooted}`;
}

/**
 * Whether a URL belongs to one of this environment's two origins.
 *
 * Parsed rather than string-matched on purpose: `startsWith` would accept
 * `https://briefings.flyevo.com.evil.example`. This function guards the
 * post-login redirect, so it is a security boundary, not a convenience.
 */
export function isKnownOrigin(url: string, origins: AppOrigins): boolean {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return false;
  }
  // `new URL('blob:https://estimator.flyevo.com/x').origin` is the *inner*
  // origin, so a blob: URL would otherwise pass. Nothing here should ever hand
  // the browser anything but a real page on one of these two hosts.
  if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') {
    return false;
  }
  return [origins.estimator, origins.briefings].some(origin => {
    try {
      return new URL(origin).origin === parsed.origin;
    } catch {
      return false;
    }
  });
}
